import React, { useMemo, useState } from "react";

import { useAuth } from "../contexts/AuthContext";
import { useScopedBoxes } from "../hooks/useScopedBoxes";
import { fmtWorkCenter } from "../lib/format";
import {
  type CopyResult,
  type ImportResult,
  type ImportRow,
  type MinMaxExport,
  copyMinmax,
  fetchWorkcenterMinmax,
  importMinmax,
} from "../services/minmaxApi";

import LoadingSpinner from "./LoadingSpinner";
import { Button } from "./ui/button";

const esc = (v: unknown): string => `"${String(v ?? "").replace(/"/g, '""')}"`;

/** Minimal CSV parser (handles quoted fields + escaped quotes). */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.some((f) => f.trim() !== "")) rows.push(row);
      row = [];
    } else field += c;
  }
  if (field !== "" || row.length) {
    row.push(field);
    if (row.some((f) => f.trim() !== "")) rows.push(row);
  }
  return rows;
}

const numOrNull = (s: string): number | null | "bad" => {
  const t = s.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= 0 ? n : "bad";
};

/**
 * Min/max management — embedded in the Inventory page for platform + vendor
 * admins (renders nothing for other roles). Three tools, all writing MTTR
 * overrides in _jcox ONLY (never the smartbox3 WMS):
 *   1. Export a location's current min/max to CSV.
 *   2. Import min/max updates for ONE location from CSV (blank min+max clears
 *      the override → reverts to the WMS value).
 *   3. Copy a source smartbox's effective min/max to one or more targets.
 */
export const MinMaxManager: React.FC = () => {
  const { token, can } = useAuth();
  const { snapshot } = useScopedBoxes();
  const canEdit = can("minmax.edit");

  const [location, setLocation] = useState("");
  const [data, setData] = useState<MinMaxExport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [parsed, setParsed] = useState<ImportRow[] | null>(null);
  const [parseInfo, setParseInfo] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importing, setImporting] = useState(false);

  const [sourceScu, setSourceScu] = useState("");
  const [targetScus, setTargetScus] = useState<Set<string>>(new Set());
  const [targetFilter, setTargetFilter] = useState("");
  const [copyResult, setCopyResult] = useState<CopyResult | null>(null);
  const [copying, setCopying] = useState(false);

  // Value is the workcenter name (what the API is keyed by); label carries
  // the smartbox code(s) in parens so duplicate-workcenter locations are
  // distinguishable. Matches the same enrichment on ReportsPage.
  const locations = useMemo(() => {
    const codesByWc = new Map<string, Set<string>>();
    for (const b of snapshot?.boxes ?? []) {
      if (!b.work_center) continue;
      if (!codesByWc.has(b.work_center)) codesByWc.set(b.work_center, new Set());
      if (b.smartbox_code) codesByWc.get(b.work_center)!.add(b.smartbox_code);
    }
    return Array.from(codesByWc.entries())
      .map(([wc, codes]) => ({
        value: wc,
        label: codes.size > 0
          ? `${wc} (${Array.from(codes).sort().join(", ")})`
          : wc,
      }))
      .sort((a, b) => a.value.localeCompare(b.value));
  }, [snapshot]);

  const boxes = useMemo(
    () =>
      [...(snapshot?.boxes ?? [])].sort((a, b) =>
        (a.smartbox_code ?? a.scu_name).localeCompare(b.smartbox_code ?? b.scu_name),
      ),
    [snapshot],
  );
  const boxLabel = (scu: string): string => {
    const b = boxes.find((x) => x.scu_name === scu);
    if (!b) return scu;
    return `${b.smartbox_code ?? b.scu_name}${b.work_center ? ` · ${fmtWorkCenter(b.work_center)}` : ""}`;
  };

  const loadLocation = (loc: string): void => {
    setLocation(loc);
    setData(null);
    setImportResult(null);
    setParsed(null);
    setParseInfo(null);
    setError(null);
    if (!loc || !token) return;
    setLoading(true);
    fetchWorkcenterMinmax(loc, token)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  };

  const exportCsv = (): void => {
    if (!data) return;
    const lines = ["item_id,sku,name,min,max,source"];
    for (const r of data.rows) {
      lines.push(
        [
          r.item_id,
          r.sku,
          r.name,
          r.effective_min ?? "",
          r.effective_suggest ?? "",
          r.has_override ? "override" : "wms",
        ]
          .map(esc)
          .join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `minmax-${location}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const onFile = async (file: File): Promise<void> => {
    setImportResult(null);
    setParsed(null);
    setParseInfo(null);
    setError(null);
    const text = await file.text();
    const grid = parseCsv(text);
    if (grid.length < 2) {
      setError("CSV has no data rows.");
      return;
    }
    const header = grid[0].map((h) => h.trim().toLowerCase());
    const ix = (name: string): number => header.indexOf(name);
    const cItem = ix("item_id");
    const cMin = ix("min");
    const cMax = ix("max");
    if (cItem < 0 || cMin < 0 || cMax < 0) {
      setError('CSV must have "item_id", "min", and "max" columns (export format).');
      return;
    }
    const rows: ImportRow[] = [];
    let bad = 0;
    for (const r of grid.slice(1)) {
      const id = Number((r[cItem] ?? "").trim());
      const mn = numOrNull(r[cMin] ?? "");
      const mx = numOrNull(r[cMax] ?? "");
      if (!Number.isInteger(id) || mn === "bad" || mx === "bad") {
        bad++;
        continue;
      }
      rows.push({ item_id: id, min_qty: mn, suggest_qty: mx });
    }
    setParsed(rows);
    setParseInfo(
      `${rows.length} valid row${rows.length === 1 ? "" : "s"}${bad ? `, ${bad} skipped (bad number/id)` : ""}.`,
    );
  };

  const applyImport = (): void => {
    if (!parsed || !location || !token) return;
    setImporting(true);
    setError(null);
    importMinmax(location, parsed, token)
      .then((res) => {
        setImportResult(res);
        setParsed(null);
        setParseInfo(null);
        loadLocation(location);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setImporting(false));
  };

  const toggleTarget = (scu: string): void =>
    setTargetScus((prev) => {
      const next = new Set(prev);
      if (next.has(scu)) next.delete(scu);
      else next.add(scu);
      return next;
    });

  const runCopy = (): void => {
    if (!sourceScu || targetScus.size === 0 || !token) return;
    setCopying(true);
    setCopyResult(null);
    setError(null);
    copyMinmax(sourceScu, Array.from(targetScus), token)
      .then(setCopyResult)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setCopying(false));
  };

  if (!canEdit) return null;

  const targetBoxes = boxes.filter(
    (b) =>
      b.scu_name !== sourceScu &&
      (targetFilter.trim() === "" ||
        boxLabel(b.scu_name).toLowerCase().includes(targetFilter.trim().toLowerCase())),
  );

  return (
    <section className="mt-8 space-y-4">
      <div>
        <h2 className="text-lg font-bold">Min/Max management</h2>
        <p className="text-xs text-secondary-500 mt-1">
          Export/import a location's min/max, or copy one smartbox's min/max to others.
          Changes are MTTR overrides only — the smartbox3 WMS is never modified.
        </p>
      </div>

      {error && (
        <div className="text-sm text-error-700 bg-error-50 px-3 py-2 rounded">{error}</div>
      )}

      {/* Export / import (one location) */}
      <div className="bg-white border border-secondary-200 rounded-md p-4">
        <h3 className="font-semibold text-secondary-800 mb-3 text-sm">
          Export / import by location
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={location}
            onChange={(e) => loadLocation(e.target.value)}
            className="px-3 py-2 min-h-[40px] border border-secondary-300 rounded-md text-sm bg-white"
          >
            <option value="" disabled>
              Select a location…
            </option>
            {locations.map((loc) => (
              <option key={loc.value} value={loc.value}>
                {loc.label}
              </option>
            ))}
          </select>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!data}>
            Export CSV
          </Button>
          <label
            className={
              "text-sm px-3 py-1.5 rounded-md border cursor-pointer " +
              (data
                ? "border-secondary-300 hover:bg-secondary-100 text-secondary-700"
                : "border-secondary-200 text-secondary-300 cursor-not-allowed")
            }
          >
            Import CSV…
            <input
              type="file"
              accept=".csv,text/csv"
              disabled={!data}
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onFile(f);
                e.target.value = "";
              }}
            />
          </label>
        </div>

        {loading && <LoadingSpinner label="loading min/max" />}

        {data && (
          <div className="text-xs text-secondary-500 mt-2">
            {data.rows.length} item{data.rows.length === 1 ? "" : "s"} at {location} (
            {data.rows.filter((r) => r.has_override).length} overridden).
          </div>
        )}

        {parseInfo && parsed && (
          <div className="mt-3 bg-secondary-50 border border-secondary-200 rounded p-3 text-sm">
            <div className="mb-2">
              {parseInfo} Blank min+max clears the override (reverts to WMS).
            </div>
            <Button size="sm" disabled={importing || parsed.length === 0} onClick={applyImport}>
              {importing ? "Applying…" : `Apply ${parsed.length} to ${location}`}
            </Button>
          </div>
        )}

        {importResult && (
          <div className="mt-3 text-sm text-success-700 bg-success-50 px-3 py-2 rounded">
            ✓ {importResult.applied} updated, {importResult.cleared} cleared
            {importResult.skipped.length > 0 &&
              `, ${importResult.skipped.length} skipped (not at this location)`}
            .
          </div>
        )}
      </div>

      {/* Copy between smartboxes */}
      <div className="bg-white border border-secondary-200 rounded-md p-4">
        <h3 className="font-semibold text-secondary-800 mb-1 text-sm">
          Copy min/max between smartboxes
        </h3>
        <p className="text-xs text-secondary-500 mb-3">
          Copies the source's current min/max to each target (for items the target also carries).
        </p>

        <label className="block text-xs uppercase tracking-wide text-secondary-500 mb-1">
          Source smartbox
        </label>
        <select
          value={sourceScu}
          onChange={(e) => {
            setSourceScu(e.target.value);
            setTargetScus((prev) => {
              const next = new Set(prev);
              next.delete(e.target.value);
              return next;
            });
            setCopyResult(null);
          }}
          className="px-3 py-2 min-h-[40px] border border-secondary-300 rounded-md text-sm bg-white w-full md:w-auto"
        >
          <option value="" disabled>
            Select source…
          </option>
          {boxes.map((b) => (
            <option key={b.scu_name} value={b.scu_name}>
              {boxLabel(b.scu_name)}
            </option>
          ))}
        </select>

        {sourceScu && (
          <>
            <div className="flex items-center justify-between mt-4 mb-1">
              <label className="block text-xs uppercase tracking-wide text-secondary-500">
                Target smartboxes ({targetScus.size} selected)
              </label>
              <input
                value={targetFilter}
                onChange={(e) => setTargetFilter(e.target.value)}
                placeholder="filter…"
                className="border border-secondary-300 rounded px-2 py-1 text-xs"
              />
            </div>
            <div className="max-h-56 overflow-auto border border-secondary-200 rounded divide-y divide-secondary-100">
              {targetBoxes.map((b) => (
                <label
                  key={b.scu_name}
                  className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-secondary-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={targetScus.has(b.scu_name)}
                    onChange={() => toggleTarget(b.scu_name)}
                  />
                  {boxLabel(b.scu_name)}
                </label>
              ))}
              {targetBoxes.length === 0 && (
                <div className="px-3 py-3 text-xs text-secondary-400">No matching smartboxes.</div>
              )}
            </div>
            <div className="mt-3">
              <Button size="sm" disabled={copying || targetScus.size === 0} onClick={runCopy}>
                {copying
                  ? "Copying…"
                  : `Copy to ${targetScus.size} smartbox${targetScus.size === 1 ? "" : "es"}`}
              </Button>
            </div>
          </>
        )}

        {copyResult && (
          <div className="mt-3 border border-secondary-200 rounded divide-y divide-secondary-100 text-sm">
            {copyResult.results.map((r) => (
              <div key={r.scu_name} className="flex items-center justify-between px-3 py-2">
                <span>{boxLabel(r.scu_name)}</span>
                {r.error ? (
                  <span className="text-error-700 text-xs">{r.error}</span>
                ) : r.note ? (
                  <span className="text-secondary-500 text-xs">{r.note}</span>
                ) : (
                  <span className="text-success-700 text-xs">{r.applied} items copied</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default MinMaxManager;
